import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import WhatsappCta from "./whatsapp-cta";
import StickyNav from "./sticky-nav";
import { Languages } from "./language-switcher";
import Logo from "./logo";
import SeasonSwitcher from "./season-switcher";

export default function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Четыре раздела, которые человек ищет глазами: туры, услуги, кто вы и
  // как связаться. Ответы на вопросы отдельным пунктом не выносим — они
  // живут на странице «Контакты и FAQ», а пятая ссылка в шапке рядом с
  // языками, сезоном и кнопкой заявки уже не помещалась.
  // Тот же список уходит в плавающую шапку и в мобильное меню —
  // расхождений между ними быть не должно.
  const links = [
    { href: "/activities", label: t("header.activities") },
    { href: "/services", label: t("header.services") },
    { href: "/reviews", label: t("header.reviews") },
    { href: "/about", label: t("header.about") },
    { href: "/contacts", label: t("header.contactUs") },
  ];

  return (
    <header className="relative text-kj-ink">
      {/* Та же навигация, но плавающая: выезжает сверху при скролле вверх */}
      <StickyNav links={links} />
      <div className="flex items-center justify-between py-5 sm:py-6">
        <Logo />

        {/* Desktop navigation — ссылки-кнопки в рамке.
            Размер меняется классом text-sm, отступы внутри — px-4 py-2. */}
        <nav className="hidden lg:flex items-center gap-x-2">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-md border border-white/15 bg-kj-dark/40 backdrop-blur-sm px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-kj-ink hover:border-kj-gold hover:text-kj-gold transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Переключатель языков и кнопка заявки.
            Раньше языки жили ТОЛЬКО в футере: японец или казах, попавший на
            страницу тура, должен был пролистать её до самого низа, чтобы
            переключить язык. Теперь выбор виден сразу на первом экране. */}
        <div className="hidden lg:flex items-center gap-x-3">
          <SeasonSwitcher />
          <Languages variant="compact" />
          <WhatsappCta
            variant="outline"
            size="default"
            className="border-kj-gold text-kj-gold bg-transparent hover:bg-kj-gold hover:text-kj-dark uppercase tracking-[0.1em] text-xs font-bold rounded-sm"
          />
        </div>

        {/* Mobile burger. p-1 давал зону нажатия ~26px при норме 44px */}
        <button
          type="button"
          className="lg:hidden -mr-2 text-kj-ink p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold"
          onClick={() => setOpen((v) => !v)}
          aria-label={t("header.menu")}
          aria-expanded={open}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <div className="lg:hidden absolute top-full left-0 right-0 z-50 bg-kj-card border border-kj-line rounded-xl p-5 flex flex-col gap-4 shadow-xl">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="rounded-md border border-kj-line px-4 py-3 text-sm font-bold uppercase tracking-[0.12em] text-kj-ink hover:border-kj-gold hover:text-kj-gold transition-colors"
            >
              {l.label}
            </a>
          ))}
          <div className="pt-1">
            <WhatsappCta
              variant="outline"
              size="default"
              className="w-full border-kj-gold text-kj-gold bg-transparent hover:bg-kj-gold hover:text-kj-dark uppercase tracking-[0.1em] text-xs font-bold rounded-sm"
            />
          </div>
          <div className="border-t border-kj-line pt-3 flex items-center justify-between gap-3">
            <Languages variant="compact" />
            <SeasonSwitcher />
          </div>
        </div>
      )}
    </header>
  );
}
